/**
 * Site header and mega menu - Figma `533:811` (the bar), `6583:23012` (the three panels,
 * desktop) and `6583:23271` (the menu on a phone), the last two reviewed by the designer
 * on 2026-10-19.
 *
 * The bar is the logo, the top-level menu items, a language pill and a dark contact
 * button. Each top-level item picks one of three panels, and the designer drew each as a
 * row of the same frame:
 *
 * - `cards`   - a row of photo cards ("Usługi", `6583:23053`)
 * - `columns` - an intro card, then titled columns of links ("O nas", `6583:23013`)
 * - `wide`    - two wide cards with a square picture and a tag ("Narzędzia online", `6583:23096`),
 *               optionally after an intro tile the size of a `cards` card (`6742:23789`)
 *
 * An item whose panel would be empty is a plain link ("Kontakt"). Why there is no fourth
 * value for that, and why `cards` is the default, is written beside `nav-item` in
 * tools/sections.spec.mjs.
 *
 * **The menu is content, not code.** It comes from the `Navigation` entry for the
 * region and language, so a Czech editor writes a Czech menu without a deploy. When
 * there is no Navigation entry yet the header falls back to listing the region's
 * published pages, which is what it did before and keeps a fresh install navigable.
 *
 * It opens on hover for a mouse and on click for touch and keyboard. Hover alone is unusable on a touch screen and
 * awkward with a keyboard; a click works everywhere, and Escape and a click outside both
 * close it. Because a mouse opens it without clicking, a top-level item with a panel and a
 * URL of its own follows that URL on a mouse click; touch and keyboard get there on a
 * second activation, once the panel is showing. **A `wide` item is the exception**: its
 * URL belongs to the intro tile alone, so its label only opens the panel, and on a phone
 * the same tile is the first one in the list.
 *
 * **Below `lg` the whole thing becomes a burger**, and the drawer it opens is the design's
 * own: each item a disclosure that drops its panel down beneath it as a stack of grey
 * cards - title, description and tag, **no pictures** - with one open at a time. The intro
 * of a `columns` panel and its column headings are not drawn there either; the design
 * lists the links on their own. The language and region controls sit behind the globe
 * button beside the close button, and the contact button becomes the last item.
 *
 * The burger itself is three CSS bars, not an exported icon. The rule that icons come
 * from Figma is about the glyph set; there is no burger in the file to export, and three
 * straight lines are geometry, not a glyph - which also lets them animate into a cross.
 * The open state's cross and globe *are* in the file, and are exported (`close`, `globe`).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from './ui/Icon';
import { Logo } from './ui/Logo';
import { AppLink } from './ui/AppLink';
import { useSite } from '../lib/site-context';
import { RegionSwitcher } from './RegionSwitcher';
import type { ApiNavCard, ApiNavItem, ApiNavigation, ApiPageSummary } from '../lib/api';
import { Image } from './ui/Image';

/** One destination in a panel, whichever list it came from. */
type Entry = Pick<ApiNavCard, 'title' | 'description' | 'url' | 'tag' | 'tagTone' | 'image'>;

/**
 * Everything an item's panel leads to, in order. `columns` reads its groups, the other
 * two their cards - the list an editor filled in for the layout they picked. The phone
 * draws exactly this; the desktop panels draw the same entries, grouped.
 */
function entriesOf(item: ApiNavItem): Entry[] {
  if (item.layout === 'columns') {
    return item.groups.flatMap((group) => group.links.map((link) => ({
      ...link, tag: null, tagTone: 'blue' as const, image: null
    })));
  }
  return item.cards;
}

/**
 * The `wide` panel's optional first tile, and the **only** place a `wide` item's own URL
 * leads: the label in the bar is a button that opens the panel, not a link. The same
 * entry is the first tile of the phone's list, so the two cannot disagree. `null` when
 * the three Intro fields are empty or the item is not `wide`.
 */
function wideIntro(item: ApiNavItem): Entry | null {
  const { intro } = item;
  if (item.layout !== 'wide' || !(intro.title || intro.text || intro.image)) return null;
  return {
    title: intro.title ?? item.label, description: intro.text, url: item.url,
    tag: null, tagTone: 'blue', image: intro.image
  };
}

const hasPanel = (item: ApiNavItem) => entriesOf(item).length > 0 || wideIntro(item) !== null;

/**
 * The tag beside a card's title - the file's `section name` instance in its menu colours
 * (`6583:23115` blue, `6583:23453` green). The file sets it at 11px, which is not a named
 * style; caption is the nearest one that is - see docs/design-tokens.md.
 */
function NavTag({ entry }: { entry: Entry }) {
  if (!entry.tag) return null;
  const tone = entry.tagTone === 'green' ? 'bg-green-100 text-green-800' : 'bg-blue-50 text-blue-600';
  return (
    <span className={`inline-flex shrink-0 items-center rounded-xxs px-2 py-1 text-caption ${tone}`}>
      {entry.tag}
    </span>
  );
}

/**
 * A card or link in a panel. With a URL it is a link and the whole surface is the
 * target; without one it is the same box, not pretending.
 */
function Target({ entry, className, onNavigate, children }: {
  entry: Entry; className: string; onNavigate: () => void; children: ReactNode;
}) {
  if (!entry.url) return <div className={className}>{children}</div>;
  return (
    <AppLink to={entry.url} onClick={onNavigate} className={`group ${className}`}>
      {children}
    </AppLink>
  );
}

const Arrow = () => (
  <Icon name="arrow-right" size={24} className="text-ink-strong transition-transform duration-300 ease-out group-hover:translate-x-3" />
);

/** `6583:23053` - five or six photo cards sharing the row. */
function CardsPanel({ item, onNavigate }: { item: ApiNavItem; onNavigate: () => void }) {
  return (
    <div className="flex gap-2.5">
      {item.cards.map((card, i) => (
        <Target
          key={i}
          entry={card}
          onNavigate={onNavigate}
          className="flex min-h-[334px] min-w-0 flex-1 flex-col gap-5 rounded-l bg-surface-subtle px-2.5 pb-4 pt-2.5 transition hover:bg-surface-muted"
        >
          {card.image ? (
            <Image media={card.image} alt="" sizes="(min-width: 1024px) 20vw, 100vw"
              className="h-[137px] w-full rounded-xs object-cover" />
          ) : null}
          <div className="flex flex-1 flex-col gap-4 px-2.5">
            <span className="text-h5 text-ink">{card.title}</span>
            {card.description ? <span className="text-caption text-ink-muted">{card.description}</span> : null}
            <span className="mt-auto pt-4"><Arrow /></span>
          </div>
        </Target>
      ))}
    </div>
  );
}

/** `6583:23013` - the intro card on the left, then up to three titled columns. */
function ColumnsPanel({ item, onNavigate }: { item: ApiNavItem; onNavigate: () => void }) {
  const { intro } = item;
  const hasIntro = Boolean(intro.title || intro.text || intro.image);

  return (
    <div className="flex items-start gap-11">
      {hasIntro ? (
        <div className="flex w-80 shrink-0 flex-col gap-7 rounded-l bg-surface-subtle px-2.5 pb-2.5 pt-5">
          <div className="flex flex-col gap-7 px-2.5">
            {intro.title ? <p className="text-h5 text-ink">{intro.title}</p> : null}
            {intro.text ? <p className="text-caption text-ink-muted">{intro.text}</p> : null}
          </div>
          {intro.image ? (
            <Image media={intro.image} alt="" sizes="320px" className="aspect-video w-full rounded-xs object-cover" />
          ) : null}
        </div>
      ) : null}

      {/* The file's three columns are 282px with 20px between them and leave the rest of
          the row empty; capping the grid keeps that rhythm on a wide screen instead of
          stretching the columns apart. */}
      <div className="grid max-w-[886px] flex-1 grid-cols-3 gap-5">
        {item.groups.map((group, i) => (
          <div key={i} className="flex flex-col gap-9">
            {group.title ? <p className="text-body text-blue-800">{group.title}</p> : null}
            {group.links.map((link, j) => (
              <Target
                key={j}
                entry={{ ...link, tag: null, tagTone: 'blue', image: null }}
                onNavigate={onNavigate}
                className="flex flex-col items-start gap-4 pr-2.5"
              >
                <span className="text-h5 text-ink-strong transition group-hover:text-blue-600">{link.title}</span>
                {link.description ? <span className="text-caption text-ink-muted">{link.description}</span> : null}
                <Arrow />
              </Target>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** `6583:23096` - two wide cards, a square picture beside the words and a tag. */
function WidePanel({ item, onNavigate }: { item: ApiNavItem; onNavigate: () => void }) {
  const { intro } = item;
  const first = wideIntro(item);

  return (
    <div className="flex gap-2.5">
      {/* The optional first tile (`6742:23789`): the same size and shape as a card of the
          `cards` panel - 218 wide, picture over title and text, the arrow at the foot - so
          it takes the item's own URL when it has one, and is a plain tile when it has not. */}
      {first ? (
        <Target
          entry={first}
          onNavigate={onNavigate}
          className="flex min-h-[334px] w-[218px] shrink-0 flex-col gap-5 rounded-l bg-surface-subtle px-2.5 pb-4 pt-2.5 transition hover:bg-surface-muted"
        >
          {intro.image ? (
            <Image media={intro.image} alt="" sizes="218px" className="h-[137px] w-full rounded-xs object-cover" />
          ) : null}
          <div className="flex flex-1 flex-col gap-4 px-2.5">
            {intro.title ? <span className="text-h5 text-ink">{intro.title}</span> : null}
            {intro.text ? <span className="text-caption text-ink-muted">{intro.text}</span> : null}
            {item.url ? <span className="mt-auto pt-4"><Arrow /></span> : null}
          </div>
        </Target>
      ) : null}
      {item.cards.map((card, i) => (
        <Target
          key={i}
          entry={card}
          onNavigate={onNavigate}
          className="flex min-h-[334px] min-w-0 flex-1 gap-4 rounded-l bg-surface-subtle py-2.5 pl-2.5 pr-4 transition hover:bg-surface-muted"
        >
          {card.image ? (
            // The picture is the card's full height, as drawn (314 square in a 334 card), so
            // the arrow sits beside its foot. A frame with the image filling it, because an
            // `img` with an aspect ratio would set the card's height instead of following it.
            <span className="relative block w-[56%] max-w-[314px] shrink-0 self-stretch">
              <Image media={card.image} alt="" sizes="314px"
                className="absolute inset-0 h-full w-full rounded-xs object-cover" />
            </span>
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col pt-2.5">
            <div className="flex items-start justify-between gap-3">
              <span className="text-h5 text-ink-strong">{card.title}</span>
              <NavTag entry={card} />
            </div>
            {card.description ? (
              <span className="mt-4 max-w-[220px] text-caption text-ink-muted">{card.description}</span>
            ) : null}
            <span className="mt-auto pt-4"><Arrow /></span>
          </div>
        </Target>
      ))}
    </div>
  );
}

function MenuPanel({ item, onNavigate }: { item: ApiNavItem; onNavigate: () => void }) {
  const Panel = item.layout === 'columns' ? ColumnsPanel : item.layout === 'wide' ? WidePanel : CardsPanel;
  return (
    // `lg:block` because a panel left open while the window narrows would otherwise sit
    // over the drawer's burger, with its own button already hidden.
    <div className="absolute inset-x-0 top-full hidden border-t border-grey-300 bg-surface shadow-sm lg:block">
      <div className="weco-container py-8">
        <Panel item={item} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

/** Three bars that become a cross. Geometry, not a glyph - see the file header. */
function Burger() {
  const bar = 'absolute left-0 h-px w-6 bg-ink-strong';
  return (
    <span aria-hidden className="relative block h-4 w-6">
      <span className={`${bar} top-0`} />
      <span className={`${bar} top-2`} />
      <span className={`${bar} top-4`} />
    </span>
  );
}

/**
 * One row of the phone menu (`Primary navigation item`, 6583:23283): the label and an
 * arrow pointing down while closed and up while open. A row with a panel is a button that
 * drops the panel beneath it; a row without one is a link.
 *
 * The rule under a row goes while it is open - the design runs the cards straight on from
 * the label, and the next row's own rule closes the group.
 */
function DrawerItem({ item, open, onToggle, onNavigate }: {
  item: ApiNavItem; open: boolean; onToggle: () => void; onNavigate: () => void;
}) {
  const row = 'flex min-h-20 w-full items-center gap-3 py-4 text-left text-h4 text-ink-strong';

  if (!hasPanel(item)) {
    return (
      <AppLink to={item.url || '/'} onClick={onNavigate} className={`${row} border-b border-grey-500`}>
        {item.label}
      </AppLink>
    );
  }

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={`${row} cursor-default ${open ? '' : 'border-b border-grey-500'}`}
      >
        <span className="flex-1">{item.label}</span>
        <Icon name="arrow-right-24" size={24} className={open ? '-rotate-90' : 'rotate-90'} />
      </button>

      {/* Rendered conditionally rather than with `hidden`: a `display: flex` class beats
          the browser's own `[hidden] { display: none }`, so the panel would stay on
          screen with its button claiming to be collapsed. */}
      {open ? (
        <ul className="flex flex-col gap-2.5 pb-2.5">
          {[wideIntro(item), ...entriesOf(item)].filter((e): e is Entry => e !== null).map((entry, i) => (
            <li key={i}>
              <Target entry={entry} onNavigate={onNavigate} className="flex flex-col gap-4 rounded-s bg-surface-subtle p-5">
                <span className="flex items-center justify-between gap-3">
                  <span className="text-body text-ink-strong">{entry.title}</span>
                  <NavTag entry={entry} />
                </span>
                {entry.description ? <span className="text-caption text-ink">{entry.description}</span> : null}
              </Target>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function LanguageLinks({ onNavigate }: { onNavigate?: () => void }) {
  const { region, locale, t } = useSite();

  // "PL" and "EN" read as two letters, not as a choice, so the landmark is named and each
  // link carries the language's own name. `aria-current` says which one you are already
  // on - the styling alone does not.
  return (
    <nav aria-label={t('a11y.chooseLanguage')} className="flex items-center gap-1">
      {region.locales.map((l) => (
        <Link
          key={l.code}
          to={`/${l.code}/`}
          lang={l.code}
          hrefLang={l.code}
          aria-current={l.code === locale ? 'true' : undefined}
          aria-label={t('a11y.languageNamed', { name: l.name })}
          onClick={onNavigate}
          className={`rounded-full px-3 py-1.5 text-caption uppercase transition ${
            l.code === locale ? 'bg-surface-muted text-ink' : 'text-ink-faint hover:text-ink'
          }`}
        >
          {l.code}
        </Link>
      ))}
    </nav>
  );
}

export default function Header({
  navigation, pages
}: { navigation: ApiNavigation | null; pages: ApiPageSummary[] }) {
  const { path, t } = useSite();
  const [open, setOpen] = useState<number | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [settings, setSettings] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  // Escape closes the panel, and so does a click anywhere outside the header.
  useEffect(() => {
    if (open === null) return;

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null); };
    const onClick = (e: MouseEvent) => {
      if (!headerRef.current?.contains(e.target as Node)) setOpen(null);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  // The drawer closes on Escape and hands focus back to the button that opened it -
  // without that, a keyboard user is dropped at the top of the document instead.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setDrawer(false);
      burgerRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer]);

  // The page behind a full-height drawer must not scroll under it.
  useEffect(() => {
    if (!drawer) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [drawer]);

  useEffect(() => { if (drawer) drawerRef.current?.focus(); }, [drawer]);

  // A drawer opened again starts folded, the way the design's first frame shows it.
  useEffect(() => { if (!drawer) { setExpanded(null); setSettings(false); } }, [drawer]);

  // Navigating closes it. Every link calls onNavigate, but a browser back button does
  // not, and a drawer left open over the new page is the kind of bug nobody reports.
  useEffect(() => { setDrawer(false); setOpen(null); }, [pathname]);

  // No menu authored yet: fall back to the region's published pages so a fresh install
  // is still navigable. Article-layout pages are templates rather than destinations, so
  // they stay out of it.
  const items: ApiNavItem[] = navigation?.items?.length
    ? navigation.items
    : pages
        .filter((p) => !p.isHomepage && p.articleLayoutFor === 'none')
        .map((p) => ({
          label: p.title, url: `/${p.slug}`, layout: 'cards',
          intro: { title: null, text: null, image: null }, groups: [], cards: []
        }));

  const closeDrawer = () => setDrawer(false);
  const cta = navigation?.ctaLabel
    ? { label: navigation.ctaLabel, url: navigation.ctaUrl ?? '/kontakt' }
    : null;

  return (
    <header
      ref={headerRef}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setOpen(null); }}
      className={`sticky top-0 z-40 border-b transition-colors ${
        drawer ? 'border-grey-400 bg-surface-subtle' : 'border-grey-300 bg-surface'
      } lg:border-grey-300 lg:bg-surface`}
    >
      <div className="weco-container flex min-h-[84px] items-center gap-x-6 gap-y-3 py-2 lg:min-h-0 lg:py-4">
        <Link
          to={path('')}
          aria-label={t('a11y.home')}
          className="shrink-0"
          onClick={() => setOpen(null)}
        >
          <Logo className="h-8 lg:h-10" />
        </Link>

        {/* Named, because the page has more than one nav landmark - this one and the
            footer's - and "navigation, navigation" is no help to a screen reader. */}
        <nav aria-label={t('a11y.mainNav')} className="hidden flex-wrap items-center gap-x-2 gap-y-1 lg:flex">
          {items.map((item, i) =>
            hasPanel(item) && item.url && item.layout !== 'wide' ? (
              // A panel item with a URL of its own. (Not `wide`: its URL belongs to the intro
              // tile, so its label is a plain button below.) A mouse has already opened the panel on
              // hover, so its click is free to follow the link. Touch and keyboard have no
              // hover: their first activation opens the panel and the second, with the panel
              // already showing, follows the link - the same two steps on either.
              <AppLink
                key={i}
                to={item.url}
                aria-expanded={open === i}
                onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpen(i); }}
                onClick={(e) => {
                  const mouse = (e.nativeEvent as PointerEvent).pointerType === 'mouse';
                  if (mouse || open === i) { setOpen(null); return; }
                  e.preventDefault();
                  setOpen(i);
                }}
                className={`rounded-full px-4 py-2 text-body-sm transition ${
                  open === i ? 'bg-surface-muted text-ink' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {item.label}
              </AppLink>
            ) : hasPanel(item) ? (
              <button
                key={i}
                type="button"
                aria-expanded={open === i}
                onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpen(i); }}
                onClick={(e) => {
                  // A mouse already opened it on hover; the click must not shut it again.
                  const mouse = (e.nativeEvent as PointerEvent).pointerType === 'mouse';
                  setOpen(mouse || open !== i ? i : null);
                }}
                // `cursor-default` undoes Tailwind preflight's pointer on every button:
                // the hand says "this goes somewhere", and only a standalone link does.
                className={`cursor-default rounded-full px-4 py-2 text-body-sm transition ${
                  open === i ? 'bg-surface-muted text-ink' : 'text-ink-muted hover:text-ink'
                }`}
              >
                {item.label}
              </button>
            ) : (
              <AppLink
                key={i}
                to={item.url || '/'}
                onClick={() => setOpen(null)}
                onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpen(null); }}
                className="rounded-full px-4 py-2 text-body-sm text-ink-muted transition hover:text-ink"
              >
                {item.label}
              </AppLink>
            )
          )}
        </nav>

        <div className="ml-auto hidden items-center gap-3 lg:flex">
          <LanguageLinks />

          <RegionSwitcher />

          {cta ? (
            <AppLink
              to={cta.url}
              onClick={() => setOpen(null)}
              className="rounded-full bg-ink-strong px-6 py-2.5 text-body-sm text-white transition hover:bg-ink"
            >
              {cta.label}
            </AppLink>
          ) : null}
        </div>

        {/* The phone's controls (6583:23275): the globe and the close button while the
            drawer is open, the burger while it is not. Both are 44px - the touch target -
            in `Radius/m`, which is what the exported frame's own corner is. */}
        <div className="ml-auto flex items-center gap-2 lg:hidden">
          {drawer ? (
            <button
              type="button"
              aria-expanded={settings}
              aria-controls="weco-drawer-settings"
              aria-label={t('a11y.languageAndRegion')}
              onClick={() => setSettings((v) => !v)}
              className={`flex h-11 w-11 items-center justify-center rounded-m border transition ${
                settings ? 'border-blue-200 bg-blue-50' : 'border-grey-400 bg-surface'
              }`}
            >
              <Icon name="globe" size={22} className="text-ink-strong" />
            </button>
          ) : null}

          <button
            ref={burgerRef}
            type="button"
            aria-expanded={drawer}
            aria-controls="weco-drawer"
            aria-label={drawer ? t('a11y.closeMenu') : t('a11y.openMenu')}
            onClick={() => setDrawer((v) => !v)}
            className={`flex h-11 w-11 items-center justify-center rounded-m transition ${
              drawer ? 'border-2 border-blue-200 bg-blue-50' : 'border border-grey-400 bg-surface'
            }`}
          >
            {drawer ? <Icon name="close" size={22} className="text-ink-strong" /> : <Burger />}
          </button>
        </div>
      </div>

      {open !== null && items[open] && hasPanel(items[open]) ? (
        <MenuPanel item={items[open]} onNavigate={() => setOpen(null)} />
      ) : null}

      {/* The drawer. Rendered inside <header>, so it is inside the same landmark as the
          menu it replaces, and a click outside it closes it through the handler above.
          It fills the screen under the 84px bar, as the design's frames do. */}
      <div
        id="weco-drawer"
        ref={drawerRef}
        tabIndex={-1}
        hidden={!drawer}
        className="absolute inset-x-0 top-full h-[calc(100dvh-84px)] overflow-y-auto bg-surface focus-visible:outline-none lg:hidden"
      >
        {settings ? (
          <div id="weco-drawer-settings" className="weco-container flex flex-wrap items-center gap-3 border-b border-grey-300 py-4">
            <LanguageLinks onNavigate={closeDrawer} />
            <RegionSwitcher />
          </div>
        ) : null}

        <nav aria-label={t('a11y.mainNav')} className="weco-container flex flex-col pb-7 pt-5">
          {items.map((item, i) => (
            <DrawerItem
              key={i}
              item={item}
              open={expanded === i}
              onToggle={() => setExpanded(expanded === i ? null : i)}
              onNavigate={closeDrawer}
            />
          ))}

          {cta ? (
            <AppLink
              to={cta.url}
              onClick={closeDrawer}
              className="flex min-h-20 items-center border-b border-grey-500 py-4 text-h4 text-ink-strong"
            >
              {cta.label}
            </AppLink>
          ) : null}
        </nav>
      </div>
    </header>
  );
}
